import React, { Component } from 'react';
import styled from 'styled-components';

// Css
import { SCREEN } from '../../../../assets/styles/constants';

// Constants
import { TABLE_LENGTH, NUMBER_OF_FIELDS } from '../lib/constants/Generic';

// Helpers
import { createBoard, findSteppableFields } from '../lib/GameHandler';

// Components
import Field from './Field';
import GamePopup from './Popup';

const GameWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-around;
`;

const GameControls = styled.span`
	margin: 0 0 10px auto;
	${SCREEN.SMALL} {
		margin: 10px 0;
		display: flex;
		justify-content: space-between;
	}
`;

const GameButton = styled.button`
	font-size: 18px;
	border: none;
	padding: 0;
	color: #908cff;
	background: none;
	cursor: pointer;
	&:first-child {
		margin-right: 20px;
	}
`;

const BoardWrapper = styled.div`
	display: flex;
	flex-direction: column;
`;

const Board = styled.div`
	display: flex;
	position: relative;
`;

const BoardRow = styled.div`
	padding: 0;
    margin: 0;
    list-style: none;
  	display: inline-block;
    line-height: 30px;
`;

const TransparentOverlay = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	z-index: 9999;
`;

// const GameStatsWrapper = styled.div`
// 	margin-left: 20px;
// `;
//
// const UndoButton = styled.button`
// 	padding: 10px 20px;
// `;

class Game extends Component {
	constructor(props) {
		super(props);
		this.steppedFields = [];
		this.previousSteppableFields = [];

		let squareLength = 40;
		let show = false;
		if (typeof window !== 'undefined') {
			const { innerWidth } = window;
			show = true;
			squareLength = Math.floor(innerWidth / TABLE_LENGTH) - 40 / TABLE_LENGTH;
		}

		this.state = {
			board: createBoard(TABLE_LENGTH),
			clicked: 0,
			undo: 0,
			victory: false,
			defeat: false,
			gameFinished: false,
			squareLength,
			show,
			timer: 0,
		};
	}

	componentWillUnmount() {
		this.resetTimer();
	}

	resetTimer = () => {
		this.pauseTimer();
		this.setState({
			timer: 0,
		});
	};

	incrementTimer = () => {
		const {
			timer,
		} = this.state;

		this.setState({
			timer: timer + 1,
		});
	};

	initializeTimer = () => {
		this.resetTimer();
		this.timer = setInterval(this.incrementTimer, 1000);
	};

	pauseTimer = () => {
		clearTimeout(this.timer);
		this.timer = null;
	};

	resetGame = () => {
		this.resetTimer();

		this.onFieldClick = this.onFirstTimeFieldClick;

		this.steppedFields = [];
		this.previousSteppableFields = [];
		this.setState({
			board: createBoard(TABLE_LENGTH),
			clicked: 0,
			undo: 0,
			victory: false,
			defeat: false,
			gameFinished: false,
		});
	};

	onFirstTimeFieldClick = (clickedField) => {
		this.initializeTimer();
		this.onFieldStep(clickedField);
		this.onFieldClick = this.inGameModeFieldClick;
	};

	inGameModeFieldClick = (clickedField) => {
		if (clickedField.canStepOn) {
			this.onFieldStep(clickedField);
		}
	};

	onFieldClick = field => this.onFirstTimeFieldClick(field);

	onFieldStep = (steppedOn) => {
		const {
			board,
			clicked,
		} = this.state;
		board[steppedOn.x][steppedOn.y].stepped = true;

		const steppableFields = findSteppableFields(board, steppedOn);

		this.previousSteppableFields.forEach((field) => {
			board[field.x][field.y].canStepOn = false;
		});

		steppableFields.forEach((field) => {
			board[field.x][field.y].canStepOn = true;
		});

		if (!steppableFields.length) {
			const leftToClick = NUMBER_OF_FIELDS - clicked;
			this.pauseTimer();
			this.setState({
				gameFinished: true,
				victory: !leftToClick,
				defeat: !!leftToClick,
			});
		} else {
			this.previousSteppableFields = steppableFields;
			this.steppedFields.push(steppedOn);

			this.setState({
				board,
				clicked: clicked + 1,
			});
		}
	};

	undoMove = () => {
		const {
			board,
			clicked,
			undo,
		} = this.state;
		this.previousSteppableFields.forEach((field) => {
			board[field.x][field.y].canStepOn = false;
		});

		const currentField = this.steppedFields.pop();
		const previousField = this.steppedFields[this.steppedFields.length - 1];

		board[currentField.x][currentField.y].stepped = false;

		if (previousField) {
			const steppableFields = findSteppableFields(board, previousField);
			steppableFields.forEach((field) => {
				board[field.x][field.y].canStepOn = true;
			});

			this.previousSteppableFields = steppableFields;

			this.setState({
				board,
				clicked: clicked - 1,
				undo: undo + 1,
			});
		}
	};

	hideModal = () => {
		this.setState({ gameFinished: false });
	};

	renderField = (field, squareLength) => (
		<Field
			squareLength={squareLength}
			key={field.id}
			onClick={() => this.onFieldClick(field)}
			canStepOn={field.canStepOn}
			stepped={field.stepped}
			current={this.steppedFields.length > 0 && field.id === this.steppedFields[this.steppedFields.length - 1].id}
		/>
	);

	renderRow = (row, index, squareLength) => (<BoardRow key={index}>{row.map(field => this.renderField(field, squareLength))}</BoardRow>);

	renderFields = () => {
		const {
			board,
			squareLength,
		} = this.state;
		return board.map((row, index) => this.renderRow(row, index, squareLength));
	};

	render() {
		const {
			clicked,
			gameFinished,
			defeat,
			victory,
			show,
		} = this.state;

		// const leftToClick = NUMBER_OF_FIELDS - clicked;

		return (
			<GameWrapper>
				{gameFinished && (
					<GamePopup
						title="End Game!"
						description={`${defeat ? 'You have lost this game.' : 'You have won this game.'} Do you want to play again?`}
						onConfirm={this.resetGame}
						onCancel={this.hideModal}
					/>
				)}
				<BoardWrapper>
					<GameControls>
						<GameButton disabled={clicked < 2 || defeat || victory} onClick={this.undoMove}>Undo</GameButton>
						<GameButton onClick={this.resetGame}>Reset Game</GameButton>
					</GameControls>
					{show && (
						<Board>
							{this.renderFields()}
							{defeat && (<TransparentOverlay onClick={this.resetGame} />)}
						</Board>
					)}
				</BoardWrapper>
				{/* <GameStatsWrapper> */}
				{/*	<GameStats */}
				{/*		clicked={clicked} */}
				{/*		gameMode={GAME_MODES.CLASSIC} */}
				{/*		leftToClick={leftToClick} */}
				{/*		undo={undo} */}
				{/*	/> */}
				{/*	<UndoButton disabled={clicked < 2 || defeat || victory} onClick={this.undoMove}> */}
				{/*		Undo */}
				{/*	</UndoButton> */}
				{/* </GameStatsWrapper> */}
			</GameWrapper>
		);
	}
}

export default Game;
